// src/pages/SurveyDeployPage.js - 설문 배포 및 관리 페이지

import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faFilter,
  faPaperPlane,
  faEye,
  faCheck,
  faTimes,
  faBell,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import WorkspaceLayout from '../components/WorkspaceLayout';
import { useAppContext } from '../context/AppContext';
import { apiFetch } from '../services/apiClient';
import './SurveyDeployPage.scss';
import { usePageHeader } from '../context/PageHeaderContext';

const SurveyDeployPage = () => {
  const { studySlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAppContext();
  
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [responseRateFilter, setResponseRateFilter] = useState({ min: '', max: '' });
  const [sendHistory, setSendHistory] = useState({}); // { userId: { sentAt: '2024-01-01', responded: false, respondedAt: null } }

  usePageHeader(
    {
      title: '설문 배포 및 관리',
      subtitle: loading ? '로딩 중...' : (study?.name || '스터디'),
      showBack: true,
      onBack: () => navigate(`/study/${studySlug}`),
    },
    [loading, study?.name, studySlug]
  );

  // 스터디 데이터 로드
  useEffect(() => {
    const loadStudy = async () => {
      try {
        const response = await apiFetch(`/studies/slug/${studySlug}`, { method: 'GET' });
        
        if (response.ok) {
          const data = await response.json();
          setStudy(data);
        }
      } catch (error) {
        console.error('스터디 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStudy();
  }, [studySlug]);

  // 유저 데이터 로드 (더미 데이터 - 실제로는 API에서 가져와야 함)
  useEffect(() => {
    // TODO: 실제 API에서 유저 데이터 가져오기
    const dummyUsers = [
      {
        id: 1,
        name: '홍길동',
        phone: '010-1234-5678',
        email: 'hong@example.com',
        participationCount: 5,
        responseRate: 85,
        lastParticipation: '2024-01-15',
        isBlacklisted: false,
        hasRecentParticipation: true,
      },
      {
        id: 2,
        name: '김철수',
        phone: '010-2345-6789',
        email: 'kim@example.com',
        participationCount: 3,
        responseRate: 60,
        lastParticipation: '2023-11-20',
        isBlacklisted: false,
        hasRecentParticipation: false,
      },
      {
        id: 3,
        name: '이영희',
        phone: '010-3456-7890',
        email: 'lee@example.com',
        participationCount: 2,
        responseRate: 40,
        lastParticipation: '2023-10-10',
        isBlacklisted: true,
        hasRecentParticipation: false,
      },
      {
        id: 4,
        name: '박민수',
        phone: '010-4567-8901',
        email: 'park@example.com',
        participationCount: 8,
        responseRate: 90,
        lastParticipation: '2024-02-01',
        isBlacklisted: false,
        hasRecentParticipation: true,
      },
    ];
    
    setUsers(dummyUsers);
    
    // TODO: 실제 API에서 발송 이력 가져오기
    // 더미 발송 이력 (일부는 발송됨, 일부는 응답함)
    const dummySendHistory = {
      1: { sentAt: '2024-01-20 10:30', responded: true, respondedAt: '2024-01-20 14:20' },
      2: { sentAt: '2024-01-20 10:30', responded: false, respondedAt: null },
      4: { sentAt: '2024-01-20 10:30', responded: true, respondedAt: '2024-01-20 11:15' },
    };
    setSendHistory(dummySendHistory);
  }, []);

  // 전화번호 마스킹
  const maskPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/-/g, '');
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}-****-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // 3개월 내 참여 여부 확인
  const isWithin3Months = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    return date >= threeMonthsAgo;
  };

  // 사용 가능한 태그 (더미 데이터)
  const availableTags = ['VIP', '신규', '활발', '비활발'];

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    return users.filter(user => {
      // 검색어 필터
      const matchesSearch = !searchTerm || 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone.includes(searchTerm) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase());

      // 태그 필터 (더미 - 실제로는 user.tags가 있어야 함)
      const matchesTags = selectedTags.length === 0 || true; // TODO: 실제 태그 필터링

      // 응답률 필터
      const matchesResponseRate = (!responseRateFilter.min || user.responseRate >= parseInt(responseRateFilter.min)) &&
                                  (!responseRateFilter.max || user.responseRate <= parseInt(responseRateFilter.max));

      return matchesSearch && matchesTags && matchesResponseRate;
    });
  }, [users, searchTerm, selectedTags, responseRateFilter]);

  // 테이블 컬럼 정의
  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          className="checkbox-input"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          disabled={row.original.isBlacklisted}
          className="checkbox-input"
        />
      ),
      size: 50,
    },
    {
      accessorKey: 'name',
      header: '이름',
      cell: ({ row }) => (
        <span style={{ fontWeight: 600, color: '#000000' }}>{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'phone',
      header: '전화번호',
      cell: ({ row }) => (
        <span style={{ color: '#000000' }}>{maskPhone(row.original.phone)}</span>
      ),
    },
    {
      accessorKey: 'email',
      header: '이메일',
      cell: ({ row }) => (
        <span style={{ color: '#000000' }}>{row.original.email || '-'}</span>
      ),
    },
    {
      accessorKey: 'recentParticipation',
      header: '3개월 내 참여',
      cell: ({ row }) => {
        const hasRecent = isWithin3Months(row.original.lastParticipation);
        return (
          <span className={`badge ${hasRecent ? 'badge-success' : 'badge-gray'}`}>
            {hasRecent ? '있음' : '없음'}
          </span>
        );
      },
    },
    {
      accessorKey: 'responseRate',
      header: '응답률',
      cell: ({ row }) => {
        const rate = row.original.responseRate;
        return (
          <div className="response-rate-cell">
            <span className="response-rate-value">{rate}%</span>
            <div className="response-rate-bar">
              <div 
                className="response-rate-fill" 
                style={{ width: `${rate}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'participationCount',
      header: '참여 횟수',
      cell: ({ row }) => (
        <span style={{ color: '#000000' }}>{row.original.participationCount}회</span>
      ),
    },
    {
      accessorKey: 'status',
      header: '상태',
      cell: ({ row }) => {
        if (row.original.isBlacklisted) {
          return <span className="badge badge-danger">블랙리스트</span>;
        }
        return <span className="badge badge-success">정상</span>;
      },
    },
    {
      accessorKey: 'sentStatus',
      header: '발송 여부',
      cell: ({ row }) => {
        const userId = row.original.id;
        const history = sendHistory[userId];
        if (!history) {
          return <span className="badge badge-gray" style={{ color: '#000000' }}>미발송</span>;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className="badge badge-success" style={{ color: '#000000' }}>발송됨</span>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>
              {new Date(history.sentAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'responseStatus',
      header: '응답 여부',
      cell: ({ row }) => {
        const userId = row.original.id;
        const history = sendHistory[userId];
        if (!history) {
          return <span style={{ color: '#000000' }}>-</span>;
        }
        if (history.responded) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span className="badge badge-success" style={{ color: '#000000' }}>응답 완료</span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                {history.respondedAt ? new Date(history.respondedAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </span>
            </div>
          );
        }
        return <span className="badge badge-gray" style={{ color: '#000000' }}>미응답</span>;
      },
    },
  ], [sendHistory]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: (row) => !row.original.isBlacklisted,
    state: {
      rowSelection: selectedUserIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {}),
    },
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === 'function' 
        ? updater(table.getState().rowSelection)
        : updater;
      const selectedIds = Object.keys(newSelection).filter(key => newSelection[key]);
      setSelectedUserIds(selectedIds.map(id => parseInt(id)));
    },
    getRowId: (row) => row.id.toString(),
  });

  // 발송 핸들러
  const handleSend = () => {
    if (!messageText || messageText.trim() === '') {
      alert('발송문구를 입력해주세요.');
      return;
    }
    
    if (selectedUserIds.length === 0) {
      alert('발송할 사용자를 선택해주세요.');
      return;
    }

    if (window.confirm(`${selectedUserIds.length}명에게 설문을 발송하시겠습니까?`)) {
      // 발송 이력 업데이트
      const now = new Date().toISOString();
      const newHistory = { ...sendHistory };
      selectedUserIds.forEach(userId => {
        newHistory[userId] = {
          sentAt: now,
          responded: false,
          respondedAt: null,
        };
      });
      setSendHistory(newHistory);

      // TODO: 실제 발송 API 호출
      alert('발송이 완료되었습니다.');
      
      // 선택 해제
      setSelectedUserIds([]);
    }
  };

  // 리마인드 발송 핸들러 (미응답자 대상)
  const handleSendReminder = () => {
    if (!messageText || messageText.trim() === '') {
      alert('발송문구를 입력해주세요.');
      return;
    }

    // 발송되었지만 응답하지 않은 사용자 찾기
    const nonRespondedUsers = users.filter(user => {
      const history = sendHistory[user.id];
      return history && !history.responded && !user.isBlacklisted;
    });

    if (nonRespondedUsers.length === 0) {
      alert('리마인드를 보낼 미응답자가 없습니다.');
      return;
    }

    if (window.confirm(`${nonRespondedUsers.length}명에게 리마인드를 발송하시겠습니까?`)) {
      // 리마인드 발송 이력 업데이트 (기존 발송 이력은 유지)
      const now = new Date().toISOString();
      const newHistory = { ...sendHistory };
      nonRespondedUsers.forEach(user => {
        if (newHistory[user.id]) {
          // 기존 발송 이력이 있으면 리마인드 횟수만 증가
          newHistory[user.id] = {
            ...newHistory[user.id],
            reminderSentAt: now,
            reminderCount: (newHistory[user.id].reminderCount || 0) + 1,
          };
        }
      });
      setSendHistory(newHistory);

      // TODO: 실제 리마인드 발송 API 호출
      alert(`리마인드가 ${nonRespondedUsers.length}명에게 발송되었습니다.`);
    }
  };

  return (
    <WorkspaceLayout hideCollapseButton={false}>
      {loading ? (
        <div className="survey-deploy-loading">
          <p>로딩 중...</p>
        </div>
      ) : (

      <div className="survey-deploy-page">

        {/* 상단 액션 바 */}
        <div className="action-bar">
          <div className="search-section">
            <div className="search-input-wrapper">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="이름, 전화번호, 이메일로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <button 
              className="btn-filter"
              onClick={() => setShowFilterModal(true)}
            >
              <FontAwesomeIcon icon={faFilter} />
              필터
            </button>
          </div>
          
          <div className="action-buttons">
            <button
              className="btn-preview"
              onClick={() => setShowPreviewModal(true)}
            >
              <FontAwesomeIcon icon={faEye} />
              미리보기
            </button>
            <button
              className="btn-edit-message"
              onClick={() => setShowPreviewModal(true)}
            >
              발송문구 보기/수정
            </button>
            <button
              className="btn-send"
              onClick={handleSend}
              disabled={selectedUserIds.length === 0 || !messageText || messageText.trim() === ''}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              발송하기 ({selectedUserIds.length})
            </button>
            <button
              className="btn-reminder"
              onClick={handleSendReminder}
              disabled={!messageText || messageText.trim() === '' || users.filter(u => {
                const h = sendHistory[u.id];
                return h && !h.responded && !u.isBlacklisted;
              }).length === 0}
              title="미응답자에게 리마인드 발송"
            >
              <FontAwesomeIcon icon={faBell} />
              리마인드 발송
            </button>
          </div>
        </div>


        {/* 테이블 */}
        <div className="table-container">
          <table className="users-table">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} className="table-header">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="table-empty">
                    검색 결과가 없습니다
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => {
                  const user = row.original;
                  const isSelected = selectedUserIds.includes(user.id);
                  
                  return (
                    <tr
                      key={row.id}
                      className={`table-row ${isSelected ? 'selected' : ''} ${user.isBlacklisted ? 'blacklisted' : ''}`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="table-cell">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 미리보기 모달 */}
        {showPreviewModal && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>발송문구 미리보기</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowPreviewModal(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body">
                <div className="message-preview">
                  <textarea
                    className="message-textarea"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="발송문구를 입력하세요..."
                    rows={10}
                  />
                  <div className="message-info">
                    <p>• 발송 대상: {selectedUserIds.length}명</p>
                    <p>• 설문 제목: {study?.name || '스크리너 설문'}</p>
                    <p>• 발송 완료: {Object.keys(sendHistory).length}명</p>
                    <p>• 응답 완료: {Object.values(sendHistory).filter(h => h.responded).length}명</p>
                    <p>• 미응답: {Object.values(sendHistory).filter(h => !h.responded).length}명</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={() => setShowPreviewModal(false)}
                >
                  취소
                </button>
                <button
                  className="btn-save"
                  onClick={() => {
                    setShowPreviewModal(false);
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 필터 모달 */}
        {showFilterModal && (
          <div className="modal-overlay">
            <div className="modal-content filter-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>필터</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowFilterModal(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body">
                {/* 태그 필터 */}
                <div className="filter-section">
                  <h3 className="filter-section-title">태그</h3>
                  <div className="filter-tags">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        className={`filter-tag ${selectedTags.includes(tag) ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedTags(prev =>
                            prev.includes(tag) 
                              ? prev.filter(t => t !== tag)
                              : [...prev, tag]
                          );
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 응답률 필터 */}
                <div className="filter-section">
                  <h3 className="filter-section-title">응답률</h3>
                  <div className="filter-range">
                    <input
                      type="number"
                      placeholder="최소"
                      min="0"
                      max="100"
                      value={responseRateFilter.min}
                      onChange={(e) => setResponseRateFilter({ ...responseRateFilter, min: e.target.value })}
                      className="filter-input"
                    />
                    <span className="filter-range-separator">~</span>
                    <input
                      type="number"
                      placeholder="최대"
                      min="0"
                      max="100"
                      value={responseRateFilter.max}
                      onChange={(e) => setResponseRateFilter({ ...responseRateFilter, max: e.target.value })}
                      className="filter-input"
                    />
                    <span className="filter-range-unit">%</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setSelectedTags([]);
                    setResponseRateFilter({ min: '', max: '' });
                    setShowFilterModal(false);
                  }}
                >
                  초기화
                </button>
                <button
                  className="btn-save"
                  onClick={() => setShowFilterModal(false)}
                >
                  적용
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </WorkspaceLayout>
  );
};

export default SurveyDeployPage;
